import type { ReactNode } from 'react'

/**
 * Strateji editörünün ortak yüzeyi — panelin diliyle aynı, haritanın üstünde
 * durabilecek kadar opak.
 *
 * Panelin cam bandı (`bg-black/25`) burada çalışmaz: editörün yüzen parçaları
 * bir zeminin değil, **uydu görüntüsünün** üstünde durur ve ince bir cam
 * bandın altından geçen parlak bir tarla yazıyı okunmaz yapar. Bu yüzden
 * ölçüler, köşeler, kenarlar ve tipografi panelden birebir alınır; değişen tek
 * şey zeminin koyuluğudur.
 *
 * Kızıl yine rasyonlu: yalnızca **etkin** araç ve seçili seçenek kızıl kenar
 * alır, yüzey rengi olarak hiçbir yerde kullanılmaz.
 */

/** Yüzen panel/araç çubuğu kabuğu. */
export const editorSurface =
  'rounded-sm border border-white/10 bg-black/70 backdrop-blur-md shadow-[0_20px_50px_-24px_rgba(0,0,0,0.95)]'

/** Açılır menü / popover kabuğu — aynı dil, biraz daha opak. */
export const editorMenu =
  'rounded-sm border border-white/10 bg-black/85 backdrop-blur-md shadow-[0_20px_50px_-24px_rgba(0,0,0,0.95)]'

/** Menü satırı. */
export const editorMenuItem =
  'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-foreground/80 transition-colors hover:bg-white/10'

export const editorMenuItemDanger =
  'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-red-400 transition-colors hover:bg-red-500/10'

/** Bölüm başlığı — panelin `Panel` başlığıyla aynı yüz, saç teli olmadan. */
export const editorLabel =
  'font-display text-[10px] font-bold tracking-[0.18em] text-muted-foreground/70'

export const editorInput =
  'w-full rounded-sm border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none'

/** Kare simge düğmesi. `active` kızıl kenarı açar — bkz. Panel'deki aynı ayrım. */
export function editorIconButton(active = false, size = 'h-8 w-8'): string {
  return `${size} flex shrink-0 cursor-pointer items-center justify-center rounded-sm border transition-colors disabled:cursor-default disabled:opacity-30 ${
    active
      ? 'border-primary/60 bg-primary/15 text-foreground'
      : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
  }`
}

/** Geniş, metinli seçenek düğmesi (çizgi stili, ok başı, zemin türü). */
export function editorChoiceButton(active: boolean): string {
  return `flex h-8 flex-1 cursor-pointer items-center justify-center rounded-sm border text-[10px] font-semibold transition-colors ${
    active
      ? 'border-primary/60 bg-primary/15 text-foreground'
      : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
  }`
}

/** Çizim paleti. Editör ve atama formu aynı dokuz rengi kullanır. */
export const COLORS = [
  { name: 'Red',    value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green',  value: '#22c55e' },
  { name: 'Cyan',   value: '#06b6d4' },
  { name: 'Blue',   value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink',   value: '#ec4899' },
  { name: 'Black',  value: '#000000' },
]

/** Etiket + gövde. `value` etiketin sağında, sayının yeri orasıdır. */
export function Field({ label, value, children }: {
  label: string
  value?: string
  children: ReactNode
}) {
  return (
    <div>
      <p className={`mb-2 flex items-baseline gap-2 ${editorLabel}`}>
        <span className="flex-1 truncate">{label}</span>
        {value && <span className="shrink-0 font-sans tracking-normal tabular-nums text-foreground/60">{value}</span>}
      </p>
      {children}
    </div>
  )
}

/**
 * Renk seçici — dokuz hazır renk + özel renk.
 *
 * Editörde dört ayrı yerde (araç varsayılanı, çokgen, çember, yazı) satır satır
 * kopyalanmıştı; biri değiştiğinde diğer üçü eskiyordu.
 */
export function ColorField({ label, custom, value, onChange, swatches = COLORS }: {
  label: string
  custom: string
  value: string
  onChange: (v: string) => void
  swatches?: { name: string; value: string }[]
}) {
  return (
    <Field label={label}>
      <div className="grid grid-cols-3 gap-1.5">
        {swatches.map(c => (
          <button
            key={c.value}
            onClick={() => onChange(c.value)}
            title={c.name}
            className={`h-7 cursor-pointer rounded-sm border transition-colors ${
              value === c.value ? 'border-white' : 'border-white/15 hover:border-white/50'
            }`}
            style={{ backgroundColor: c.value }}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <label className="relative cursor-pointer">
          <input
            type="color"
            value={value}
            onChange={e => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <div
            className="h-7 w-10 rounded-sm border border-white/15 transition-colors hover:border-white/50"
            style={{ backgroundColor: value }}
          />
        </label>
        <span className={editorLabel}>{custom}</span>
      </div>
    </Field>
  )
}

/** Sayısal ayar — değeri etikete yazar, çünkü sürgünün kendisi sayıyı söylemez. */
export function SliderField({ label, value, display, min, max, step = 1, onChange }: {
  label: string
  value: number
  display: string
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
}) {
  return (
    <Field label={label} value={display}>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={e => onChange(+e.target.value)}
        className="w-full cursor-pointer accent-primary"
      />
    </Field>
  )
}

/** Birkaç seçenekten biri — çizgi stili, ok başı, yazı zemini. */
export function ChoiceField<T extends string>({ label, value, options, onChange }: {
  label: string
  value: T
  options: { value: T; label: string; preview?: ReactNode }[]
  onChange: (v: T) => void
}) {
  return (
    <Field label={label}>
      <div className="flex gap-1.5">
        {options.map(o => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            title={o.label}
            className={editorChoiceButton(value === o.value)}
          >
            {o.preview ?? o.label}
          </button>
        ))}
      </div>
    </Field>
  )
}

/** Çizgi stili önizlemesi — üç panelde de aynı çizim. */
export function lineStylePreview(style: 'solid' | 'dashed' | 'dotted'): ReactNode {
  return (
    <svg width="40" height="8" viewBox="0 0 40 8" aria-hidden>
      {style === 'solid' && <line x1="2" y1="4" x2="38" y2="4" stroke="currentColor" strokeWidth="2" />}
      {style === 'dashed' && <line x1="2" y1="4" x2="38" y2="4" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" />}
      {style === 'dotted' && <line x1="2" y1="4" x2="38" y2="4" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 5" />}
    </svg>
  )
}

export function lineStyleOptions(T: {
  solid: string; dashed: string; dotted: string
}): { value: 'solid' | 'dashed' | 'dotted'; label: string; preview: ReactNode }[] {
  return [
    { value: 'solid',  label: T.solid,  preview: lineStylePreview('solid') },
    { value: 'dashed', label: T.dashed, preview: lineStylePreview('dashed') },
    { value: 'dotted', label: T.dotted, preview: lineStylePreview('dotted') },
  ]
}
